// frontend/src/admin/pages/AdminPricingPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import {
  listPricing,
  setPlanPrice,
  schedulePlanPrice,
  cancelScheduledPrice,
  createPromo,
  endPromo,
} from "../api";

const CADENCES = ["monthly", "annual"];

/** Cents → "€59.00" (or "Free" for 0). QRFlow sells in EUR only. */
function fmtCents(cents) {
  if (cents === null || cents === undefined) return "—";
  if (cents === 0) return "Free";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "EUR",
  });
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** A euros input that round-trips cents without float drift. */
function DollarsInput({ valueCents, onChangeCents, id }) {
  const euros = valueCents === "" ? "" : (valueCents / 100).toString();
  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">
        €
      </span>
      <input
        id={id}
        type="number"
        min="0"
        step="0.01"
        value={euros}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") return onChangeCents("");
          onChangeCents(Math.round(parseFloat(v) * 100));
        }}
        className="w-32 rounded-md border border-slate-300 pl-5 pr-2 py-1.5 text-sm"
      />
    </div>
  );
}
DollarsInput.propTypes = {
  valueCents: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onChangeCents: PropTypes.func.isRequired,
  id: PropTypes.string,
};
DollarsInput.defaultProps = { id: undefined };

/** Promo sub-panel: shows an active promo (with End) or a create-promo form. */
function PromoSection({ plan, cadence, onChanged, onError }) {
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [applyExisting, setApplyExisting] = useState(false);
  const [busy, setBusy] = useState(false);

  const promo = plan.active_promo;

  const create = async () => {
    setBusy(true);
    try {
      await createPromo(plan.plan_id, {
        discount_type: type,
        // percent → whole number; amount → euros to cents.
        discount_value:
          type === "percent"
            ? Number(value)
            : Math.round(parseFloat(value) * 100),
        billing_period: cadence,
        apply_to_existing: applyExisting,
        effective_from: new Date(from).toISOString(),
        effective_until: new Date(until).toISOString(),
      });
      setValue("");
      setFrom("");
      setUntil("");
      onChanged();
    } catch (e) {
      onError(e?.response?.data?.detail || "Failed to create promo.");
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    try {
      await endPromo(promo.id);
      onChanged();
    } catch (e) {
      onError(e?.response?.data?.detail || "Failed to end promo.");
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        Promotion
      </h4>
      {promo ? (
        <div className="flex items-center justify-between text-sm rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
          <span className="text-amber-800">
            Active: <strong>{fmtCents(promo.unit_amount)}</strong>
            {promo.effective_until
              ? ` until ${fmtDate(promo.effective_until)}`
              : ""}
          </span>
          <button
            type="button"
            onClick={end}
            className="text-xs font-semibold text-rose-600 hover:text-rose-800"
          >
            End now
          </button>
        </div>
      ) : (
        <div className="flex items-end gap-2 flex-wrap text-sm">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1.5"
          >
            <option value="percent">% off</option>
            <option value="amount">€ off</option>
          </select>
          <input
            type="number"
            min="0"
            step={type === "percent" ? "1" : "0.01"}
            value={value}
            placeholder={type === "percent" ? "30" : "10.00"}
            onChange={(e) => setValue(e.target.value)}
            className="w-20 rounded-md border border-slate-300 px-2 py-1.5"
            aria-label="Discount value"
          />
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1.5"
            aria-label="Promo start"
          />
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1.5"
            aria-label="Promo end"
          />
          <button
            type="button"
            disabled={busy || !value || !from || !until}
            onClick={create}
            className="rounded-md bg-amber-600 px-3 py-1.5 font-semibold text-white hover:bg-amber-700 disabled:opacity-40"
          >
            Start promo
          </button>
          <label className="flex w-full items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={applyExisting}
              onChange={(e) => setApplyExisting(e.target.checked)}
            />
            Also apply to existing {cadence} subscribers (their next invoices are
            discounted; reverts when the promo ends).
          </label>
        </div>
      )}
    </div>
  );
}
PromoSection.propTypes = {
  plan: PropTypes.object.isRequired,
  cadence: PropTypes.string.isRequired,
  onChanged: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

/** Per-plan row: shows current/upcoming, and edit + schedule forms. */
function PlanRow({ plan, cadence, onChanged, onError }) {
  const [amountCents, setAmountCents] = useState(
    plan.current.unit_amount ?? "",
  );
  const [note, setNote] = useState("");
  const [when, setWhen] = useState("");
  const [migrateExisting, setMigrateExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'now' | 'schedule' | null

  const current = plan.current;
  // Reset the amount field when the cadence slice changes.
  useEffect(() => {
    setAmountCents(plan.current.unit_amount ?? "");
  }, [plan.current.unit_amount]);

  const doSetNow = async () => {
    setBusy(true);
    try {
      await setPlanPrice(plan.plan_id, {
        unit_amount: Number(amountCents),
        billing_period: cadence,
        note: note || undefined,
      });
      setConfirming(null);
      setNote("");
      onChanged();
    } catch (e) {
      onError(e?.response?.data?.detail || "Failed to set price.");
    } finally {
      setBusy(false);
    }
  };

  const doSchedule = async () => {
    setBusy(true);
    try {
      await schedulePlanPrice(plan.plan_id, {
        unit_amount: Number(amountCents),
        billing_period: cadence,
        effective_from: new Date(when).toISOString(),
        note: note || undefined,
        migrate_existing: migrateExisting,
      });
      setConfirming(null);
      setNote("");
      setWhen("");
      onChanged();
    } catch (e) {
      onError(e?.response?.data?.detail || "Failed to schedule change.");
    } finally {
      setBusy(false);
    }
  };

  const cancelRow = async (rowId) => {
    try {
      await cancelScheduledPrice(rowId);
      onChanged();
    } catch (e) {
      onError(e?.response?.data?.detail || "Failed to cancel.");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-slate-900">
            {plan.plan_name}
          </h3>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xl font-bold text-slate-900">
              {fmtCents(current.unit_amount)}
            </span>
            <span className="text-sm text-slate-500">
              / {cadence === "annual" ? "year" : "month"}
            </span>
            {current.is_promo && (
              <span className="rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5">
                PROMO
              </span>
            )}
          </div>
          {plan.upcoming && (
            <p className="mt-2 text-sm text-indigo-700">
              Starting {fmtDate(plan.upcoming.effective_from)}:{" "}
              <strong>{fmtCents(plan.upcoming.unit_amount)}</strong>
            </p>
          )}
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label
              htmlFor={`amt-${plan.plan_id}`}
              className="block text-xs font-medium text-slate-500 mb-1"
            >
              New price
            </label>
            <DollarsInput
              id={`amt-${plan.plan_id}`}
              valueCents={amountCents}
              onChangeCents={setAmountCents}
            />
          </div>
          <div>
            <label
              htmlFor={`when-${plan.plan_id}`}
              className="block text-xs font-medium text-slate-500 mb-1"
            >
              Effective date (blank = now)
            </label>
            <input
              id={`when-${plan.plan_id}`}
              type="date"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={busy || amountCents === ""}
            onClick={() => setConfirming(when ? "schedule" : "now")}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {when ? "Schedule" : "Set now"}
          </button>
        </div>
      </div>

      {/* Optional note */}
      <input
        type="text"
        value={note}
        placeholder="Optional note (audit memo)"
        onChange={(e) => setNote(e.target.value)}
        className="mt-3 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs"
      />

      {/* Confirmation */}
      {confirming && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm">
          {confirming === "now" ? (
            <p>
              Set <strong>{plan.plan_name}</strong> to{" "}
              <strong>{fmtCents(Number(amountCents))}</strong> effective
              immediately? New checkouts bill this at once; existing subscribers
              are unaffected until migrated.
            </p>
          ) : (
            <div>
              <p>
                Schedule <strong>{plan.plan_name}</strong> to{" "}
                <strong>{fmtCents(Number(amountCents))}</strong> starting{" "}
                <strong>{fmtDate(when)}</strong>?
              </p>
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={migrateExisting}
                  onChange={(e) => setMigrateExisting(e.target.checked)}
                />
                Migrate existing subscribers on that date (advance-notice email
                is sent automatically). Uncheck for new customers only.
              </label>
            </div>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={confirming === "now" ? doSetNow : doSchedule}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-40"
            >
              {busy ? "Working…" : "Confirm"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Scheduled changes */}
      {plan.scheduled.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Scheduled changes
          </h4>
          <ul className="space-y-1">
            {plan.scheduled.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between text-sm"
              >
                <span>
                  {fmtCents(s.unit_amount)} from{" "}
                  {fmtDate(s.effective_from)}
                  {s.note ? ` — ${s.note}` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => cancelRow(s.id)}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-800"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(current.unit_amount ?? 0) > 0 && (
        <PromoSection
          plan={plan}
          cadence={cadence}
          onChanged={onChanged}
          onError={onError}
        />
      )}
    </div>
  );
}
PlanRow.propTypes = {
  plan: PropTypes.object.isRequired,
  cadence: PropTypes.string.isRequired,
  onChanged: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default function AdminPricingPage() {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState("");
  const [cadence, setCadence] = useState("monthly");

  const load = useCallback(() => {
    listPricing(cadence)
      .then((d) => setPlans(d.plans))
      .catch(() => setError("Failed to load pricing."));
  }, [cadence]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="max-w-4xl">
      <header className="mb-5">
        <h1 className="text-xl font-bold text-slate-900">Pricing &amp; Plans</h1>
        <p className="text-sm text-slate-500 mt-1">
          Set a tier&apos;s price now or schedule a change for a future date. A
          new Stripe price is created and used the moment it takes effect.
          QRFlow sells in EUR only.
        </p>
      </header>

      {/* Cadence selector — scopes every control below. */}
      <div className="mb-5 flex flex-wrap items-center gap-4">
        <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-slate-200">
          {CADENCES.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={cadence === p}
              onClick={() => setCadence(p)}
              className={
                cadence === p
                  ? "bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white capitalize"
                  : "bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 capitalize"
              }
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 px-4 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {plans === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <PlanRow
              key={plan.plan_id}
              plan={plan}
              cadence={cadence}
              onChanged={() => {
                setError("");
                load();
              }}
              onError={setError}
            />
          ))}
        </div>
      )}
    </div>
  );
}
